import { useEffect, useRef, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

/**
 * Client uploads workout videos → trainer reviews them with notes.
 * Videos are streamed from /api/files/{path} which auto-authenticates via
 * same-origin cookies.
 */
export default function VideoReviewsTab({ clientId, user }) {
  const isTrainer = user.role === "trainer";
  const [videos, setVideos] = useState([]);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get(`/clients/${clientId}/videos`); setVideos(data); }
    catch { toast.error("Failed to load videos"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const upload = async (files) => {
    const file = files?.[0]; if (!file) return;
    if (file.size > 500 * 1024 * 1024) return toast.error("File too large (max 500MB)");
    setUploading(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      await api.post(`/clients/${clientId}/videos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      toast.success("Video uploaded");
      setCaption(""); if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    setUploading(false); setProgress(0);
  };

  const saveReview = async (videoId, note) => {
    try {
      await api.put(`/clients/${clientId}/videos/${videoId}/review`, { note });
      toast.success("Review saved");
      load();
    } catch { toast.error("Failed to save review"); }
  };

  const removeVideo = async (videoId) => {
    if (!window.confirm("Delete this video?")) return;
    try { await api.delete(`/clients/${clientId}/videos/${videoId}`); load(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="max-w-4xl space-y-8">
      <div>
        <div className="font-display text-3xl uppercase">Training Video Reviews</div>
        <div className="text-xs text-zinc-500 uppercase tracking-[0.2em] mt-1">
          {isTrainer ? "Videos your clients upload for form review." : "Upload a clip. Get personalized form feedback from your coach."}
        </div>
      </div>

      {/* Client upload area */}
      {!isTrainer && (
        <div className="border border-zinc-800 p-6">
          <div className="font-display text-xl uppercase mb-4">Upload a Video</div>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 block mb-2">Caption (what should coach look at?)</label>
              <Input data-testid="video-caption"
                value={caption} onChange={(e) => setCaption(e.target.value)}
                placeholder="e.g. Squat form check — 3rd set, 315 lb"
                className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-11" />
            </div>
            <div>
              <input ref={fileRef} type="file" accept="video/*"
                data-testid="video-file-input"
                onChange={(e) => upload(Array.from(e.target.files || []))}
                disabled={uploading}
                className="block w-full text-xs text-zinc-400 file:mr-4 file:py-3 file:px-6 file:border-0 file:bg-[#FF3B30] file:text-white file:uppercase file:tracking-[0.2em] file:text-[11px] hover:file:bg-[#D63028] file:cursor-pointer" />
              <div className="text-[10px] text-zinc-600 uppercase tracking-[0.25em] mt-2">MP4, MOV, WEBM · Max 500MB · Only you and your coach can see it</div>
              {uploading && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1" data-testid="video-progress">Uploading {progress}%</div>
                  <div className="h-1 bg-zinc-800"><div className="h-full bg-[#FF3B30] transition-all" style={{ width: `${progress}%` }} /></div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Video list */}
      <div>
        <div className="font-display text-2xl uppercase mb-4">History</div>
        {videos.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-16 text-center text-zinc-500 text-sm">
            {isTrainer ? "No videos uploaded yet. Your client will see an upload button on their side." : "No videos yet. Upload your first clip above."}
          </div>
        ) : (
          <div className="space-y-6">
            {videos.map((v) => (
              <VideoCard key={v.id} video={v} isTrainer={isTrainer} onSaveReview={saveReview} onDelete={removeVideo} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function VideoCard({ video, isTrainer, onSaveReview, onDelete }) {
  const [reviewOpen, setReviewOpen] = useState(false);
  const [draft, setDraft] = useState(video.review_note || "");
  const src = `${API}/files/${video.storage_path}`;

  return (
    <div className="border border-zinc-800 bg-[#0A0A0A]" data-testid={`video-${video.id}`}>
      <div className="grid md:grid-cols-2 gap-0">
        <div className="bg-black">
          <video controls preload="metadata" data-testid={`video-player-${video.id}`}
                 src={src}
                 className="w-full h-full max-h-[420px] object-contain" />
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] font-mono">
                {new Date(video.created_at).toLocaleString()}
              </div>
              {video.caption && (
                <div className="text-zinc-200 text-sm mt-2 whitespace-pre-wrap">{video.caption}</div>
              )}
            </div>
            <button onClick={() => onDelete(video.id)} data-testid={`video-delete-${video.id}`}
                    title="Delete"
                    className="text-zinc-600 hover:text-[#FF3B30] text-xs w-7 h-7 grid place-items-center border border-zinc-800 hover:border-[#FF3B30] transition">
              ✕
            </button>
          </div>

          {video.review_note ? (
            <div className="bg-[#1A1A1A] border-l-2 border-[#FF3B30] p-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF3B30] mb-1">Coach review</div>
              <p className="text-sm text-zinc-200 whitespace-pre-wrap">{video.review_note}</p>
              {video.review_note_at && (
                <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-600 mt-2">
                  {new Date(video.review_note_at).toLocaleString()}
                </div>
              )}
            </div>
          ) : !isTrainer && (
            <div className="text-xs text-zinc-600 italic">No review yet. Your coach will get to it soon.</div>
          )}

          {isTrainer && (
            reviewOpen ? (
              <div className="space-y-2">
                <Textarea data-testid={`video-review-textarea-${video.id}`} rows={4}
                          value={draft} onChange={(e) => setDraft(e.target.value)}
                          placeholder="Form cues, corrections, what's improving..."
                          className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white text-sm" />
                <div className="flex gap-2">
                  <Button data-testid={`video-save-review-${video.id}`}
                          size="sm"
                          onClick={() => { onSaveReview(video.id, draft); setReviewOpen(false); }}
                          className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-xs uppercase tracking-[0.2em]">
                    Save Review
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setReviewOpen(false); setDraft(video.review_note || ""); }}
                          className="rounded-none border-zinc-800 bg-transparent text-xs uppercase tracking-[0.2em] hover:bg-zinc-900 hover:text-white">
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button onClick={() => setReviewOpen(true)}
                      data-testid={`video-open-review-${video.id}`}
                      variant="outline"
                      size="sm"
                      className="rounded-none border-zinc-800 bg-transparent text-xs uppercase tracking-[0.2em] hover:bg-zinc-900 hover:text-white">
                {video.review_note ? "Edit Review" : "Add Review"}
              </Button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
